/**
 * useAxlSupply — V2.K.6
 *
 * Fetches AXL supply data from CoinGecko.
 *
 * Axelar (AXL) tokenomics in a nutshell :
 *   - **Uncapped** : no max supply, inflation funds validator
 *     rewards. Base 8 % + 0.75 % per externally-verified chain →
 *     cumulated ~11.5 % at writing.
 *   - **Cobalt upgrade (Feb 2025)** : 98 % of cross-chain transaction
 *     fees are burned, 2 % flow to the community grant pool. This is
 *     the principal deflationary lever — AXL becomes net deflationary
 *     when fee burn > inflation mint.
 *   - The original 1 B AXL allocation finishes vesting in 2026.
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface AxlSupplyData {
  circulating: number;
  total: number;
  /** Published cumulated inflation rate (base + per-chain bonus). */
  annualInflationPct: number;
  /** Estimated annual mint at the current rate. */
  annualMint: number;
  /** Share of cross-chain fees burned (Cobalt upgrade, Feb 2025). */
  feeBurnPct: number;
  lastUpdated: string | null;
}

// Reference values verified from axelar.network/blog/cobalt-tokenomics-
// upgrade, axelar.network/blog/introducing-the-axl-token, and
// figment.io/insights/axelar-tokenomics. The 11.5 % cumulated rate
// reflects base inflation + per-chain validation bonuses; the
// announced governance proposal would lower this toward ~6.7 %.
const AXL_ANNUAL_INFLATION_PCT_REFERENCE = 11.5;
const AXL_FEE_BURN_PCT_REFERENCE = 98;

async function fetchAxlSupply(): Promise<AxlSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/axelar?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || circulating;

  const annualMint = circulating * (AXL_ANNUAL_INFLATION_PCT_REFERENCE / 100);

  return {
    circulating,
    total,
    annualInflationPct: AXL_ANNUAL_INFLATION_PCT_REFERENCE,
    annualMint,
    feeBurnPct: AXL_FEE_BURN_PCT_REFERENCE,
    lastUpdated: json.last_updated || null,
  };
}

export function useAxlSupply() {
  return useQuery({
    queryKey: ['axl', 'supply'],
    queryFn: fetchAxlSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
